import React, { useEffect } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { Button } from '@mantine/core';
import { IconArrowNarrowLeft } from '@tabler/icons-react';
import ItemGallery from './ItemGallery';
import ShopCard from './ShopCard';
import { contactDetails } from '../../../Data/data';
import { byShopOrder, typeLabel, shopTypeUrl } from '../Shop_Functions/catalog';

const RELATED_COUNT = 3;

// Other pieces of the same type, in shop order; any pieces if there are none of this type
const relatedPieces = (items, item) => {
    const others = items.filter((other) => other.id !== item.id).sort(byShopOrder);
    const sameType = others.filter((other) => other.product_type === item.product_type);
    return sameType.length
        ? { heading: `More ${item.product_type}`, pieces: sameType.slice(0, RELATED_COUNT) }
        : { heading: 'More from the shop', pieces: others.slice(0, RELATED_COUNT) };
};

const ItemDisplay = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { items } = useOutletContext();
    const item = items.find((piece) => String(piece.id) === productId);

    // Moving between pieces (e.g. from "More bowls") starts at the top
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [productId]);

    useEffect(() => {
        if (item) document.title = `${item.title} · Juli Juneau`;
    }, [item]);

    if (!item) {
        return (
            <div className='flex w-full justify-center pb-16'>
                <div className='w-full max-w-[1100px] px-4 flex flex-col items-start gap-4 pt-16'>
                    <div className='text-white text-2xl font-light uppercase'>This piece is no longer available</div>
                    <div className='text-zinc-400'>It may have been sold or removed from the shop.</div>
                    <Link to='/shop/main' className='text-white underline underline-offset-4'>Browse the shop</Link>
                </div>
            </div>
        );
    }

    const related = relatedPieces(items, item);

    return (
        <div className='flex w-full justify-center pb-16'>
            <div className='w-full max-w-[1100px] px-4 flex flex-col'>
                <nav aria-label='Breadcrumb' className='pt-8 pb-6'>
                    <ol className='flex items-center gap-2 m-0 p-0 list-none text-sm text-zinc-500'>
                        <li>
                            <Link to='/shop/main' className='inline-flex items-center gap-1 text-zinc-500 no-underline transition-colors hover:text-white'>
                                <IconArrowNarrowLeft size={18} stroke={1.75} aria-hidden='true' />
                                <span>Shop</span>
                            </Link>
                        </li>
                        <li aria-hidden='true'>/</li>
                        <li>
                            <Link to={shopTypeUrl(item.product_type)} className='text-zinc-300 no-underline transition-colors hover:text-white'>
                                {typeLabel(item.product_type)}
                            </Link>
                        </li>
                    </ol>
                </nav>

                <div className='grid grid-cols-1 820:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-8 820:gap-12 items-start'>
                    <ItemGallery photos={item.image_url_arr} title={item.title} />

                    {/* Details stay in view beside the photos on wide screens */}
                    <div className='820:sticky 820:top-16 flex flex-col gap-6'>
                        <div>
                            <h1 className='m-0 text-white text-2xl font-light uppercase tracking-wide'>{item.title}</h1>
                            <div className='mt-2 text-zinc-300 text-lg'>$ {item.price}</div>
                        </div>

                        {item.description &&
                            <p className='m-0 text-zinc-300 leading-relaxed'>{item.description}</p>
                        }

                        <dl className='grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 m-0 py-4 text-sm border-solid border-0 border-y border-zinc-800'>
                            {item.size && <>
                                <dt className='text-zinc-500'>Size</dt>
                                <dd className='m-0 text-white'>{item.size}</dd>
                            </>}
                            <dt className='text-zinc-500'>Type</dt>
                            <dd className='m-0 text-white'>{typeLabel(item.product_type)}</dd>
                            <dt className='text-zinc-500'>Artist</dt>
                            <dd className='m-0 text-white'>Juli Juneau, New Orleans</dd>
                        </dl>

                        <div className='flex flex-col gap-3'>
                            <Button
                                size='md'
                                fullWidth
                                onClick={() => navigate('/contact', { state: { item: item.title } })}
                                styles={{
                                    root: {
                                        backgroundColor: '#ffffff',
                                        color: '#000000',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        '&:not([data-disabled]):hover': { backgroundColor: '#e4e4e7' },
                                    },
                                }}
                            >
                                Ask about this piece
                            </Button>
                            <p className='m-0 text-zinc-500 text-sm leading-relaxed'>
                                Pricing is confirmed and shipping arranged directly with Juli.
                            </p>
                            <a href={contactDetails.phoneHref} className='self-start text-sm text-zinc-300 underline underline-offset-4 hover:text-white'>
                                Or call or text {contactDetails.phoneDisplay}
                            </a>
                        </div>
                    </div>
                </div>

                {related.pieces.length > 0 &&
                    <section aria-labelledby='related-heading' className='mt-16 pt-10 border-solid border-0 border-t border-zinc-800'>
                        <h2 id='related-heading' className='m-0 mb-6 text-white text-xl font-light uppercase tracking-wide'>
                            {related.heading}
                        </h2>
                        <div className='grid grid-cols-3 md:grid-cols-2 sm:grid-cols-2 gap-6 sm:gap-3'>
                            {related.pieces.map((piece) => (
                                <ShopCard key={piece.id} item={piece} to={`/shop/main/${piece.id}`} />
                            ))}
                        </div>
                    </section>
                }
            </div>
        </div>
    )
}

export default ItemDisplay
